// src/components/GiftCardRedeem.jsx
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Gift, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../lib/api";

export default function GiftCardRedeem() {
  const navigate = useNavigate();
  const { code: urlCode } = useParams();
  const [giftCardCode, setGiftCardCode] = useState(urlCode || "");
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [giftCardData, setGiftCardData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (urlCode) {
      handleVerify();
    }
  }, [urlCode]);

  const handleVerify = async () => {
    if (!giftCardCode.trim()) {
      setError("Please enter a gift card code");
      return;
    }

    setVerifying(true);
    setError("");

    try {
      const data = await api.giftCards.verify(giftCardCode);
      setGiftCardData(data.giftCard);
    } catch (err) {
      setError(err.message);
      setGiftCardData(null);
    } finally {
      setVerifying(false);
    }
  };

  const handleRedeem = async () => {
    setLoading(true);
    setError("");

    try {
      await api.giftCards.redeem(giftCardCode);

      navigate("/book", {
        state: {
          giftCard: giftCardData,
          giftCardCode: giftCardCode,
        },
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate("/")}
            className="text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            ← Back to Home
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Gift className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Redeem Gift Card</h1>
              <p className="text-gray-600 mt-1">Enter your gift card code to get started</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Gift Card Code Input */}
        {!giftCardData && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Enter Gift Card Code</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="giftCardCode">Gift Card Code</Label>
                <Input
                  id="giftCardCode"
                  placeholder="GIFT-XXXX-XXXX-XXXX"
                  value={giftCardCode}
                  onChange={(e) => setGiftCardCode(e.target.value.toUpperCase())}
                  className="font-mono"
                  disabled={verifying}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the code from your gift card email
                </p>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              <Button
                onClick={handleVerify}
                disabled={verifying || !giftCardCode.trim()}
                className="w-full"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify Gift Card"
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Gift Card Details */}
        {giftCardData && (
          <>
            <Card className="border-emerald-200 mb-6">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-emerald-600" />
                  <CardTitle className="text-emerald-900">Valid Gift Card</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Gift Card Value */}
                <div className="p-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl text-white">
                  <p className="text-sm font-medium mb-2">Gift Card Value</p>
                  <p className="text-4xl font-bold">${giftCardData.amount}</p>
                  {giftCardData.remainingBalance < giftCardData.amount && (
                    <p className="text-sm mt-2 text-emerald-100">
                      Remaining: ${giftCardData.remainingBalance}
                    </p>
                  )}
                </div>

                {/* Service Details */}
                {giftCardData.serviceType && (
                  <div className="p-4 bg-emerald-50 rounded-lg">
                    <p className="font-semibold text-emerald-900">
                      {giftCardData.serviceType}
                    </p>
                    {giftCardData.duration && (
                      <p className="text-sm text-emerald-700 mt-1">
                        {giftCardData.duration}
                      </p>
                    )}
                  </div>
                )}

                {/* Gift Card Info */}
                <div className="space-y-2 text-sm">
                  {giftCardData.recipientName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Recipient:</span>
                      <span className="font-medium">{giftCardData.recipientName}</span>
                    </div>
                  )}
                  {giftCardData.senderName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">From:</span>
                      <span className="font-medium">{giftCardData.senderName}</span>
                    </div>
                  )}
                  {giftCardData.expirationDate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expires:</span>
                      <span className="font-medium">
                        {new Date(giftCardData.expirationDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="font-medium text-emerald-600">
                      {giftCardData.status === "active" ? "Active" : giftCardData.status}
                    </span>
                  </div>
                </div>

                {/* Personal Message */}
                {giftCardData.personalMessage && (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Personal Message:
                    </p>
                    <p className="text-sm text-gray-600 italic">
                      "{giftCardData.personalMessage}"
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Redeem Action */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Ready to Book Your Massage?
                  </h3>
                  <p className="text-sm text-gray-600">
                    Click below to start booking your massage appointment using this gift card
                  </p>
                  <Button
                    size="lg"
                    onClick={handleRedeem}
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      "Book Appointment with Gift Card"
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setGiftCardData(null);
                      setGiftCardCode("");
                      setError("");
                    }}
                    className="w-full"
                  >
                    Try Different Code
                  </Button>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Help Section */}
        <Card className="mt-6 bg-gray-50">
          <CardContent className="pt-6">
            <h3 className="font-semibold text-gray-900 mb-3">Need Help?</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>• Gift card codes are sent via email</li>
              <li>• Codes are case-insensitive and can include dashes</li>
              <li>• Gift cards are valid for 1 year from purchase date</li>
              <li>• Contact support if you're having trouble redeeming</li>
            </ul>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Questions? Contact us at{" "}
                <a
                  href="mailto:info@bookatherapy.com"
                  className="text-emerald-600 hover:underline"
                >
                  info@bookatherapy.com
                </a>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
